import z from 'zod';
import { optionNumberSchema, optionSchema } from '~/features/types';

/**
 * A city/ZIP picked from `GET /geographies/search`. The coordinates travel with the option so
 * Search can send them without a second lookup.
 */
export const providerJobSearchLocationSchema = z.object({
  value: z.string(),
  label: z.string(),
  latitude: z.number(),
  longitude: z.number(),
});

export type ProviderJobSearchLocation = z.infer<typeof providerJobSearchLocationSchema>;

export const providerJobSearchFilterSchema = z.object({
  providerCategory: optionNumberSchema.nullish(),
  aoiSpecialty: optionSchema.nullish(),
  subspecialties: z.array(optionSchema).optional(),
  states: z.array(optionSchema).optional(),
  workLocation: z.array(optionSchema).optional(),
  jobType: z.array(optionSchema).optional(),
  employmentClassification: z.array(optionSchema).optional(),
  location: providerJobSearchLocationSchema.nullish(),
  radiusMiles: optionSchema.nullish(),
});

export type ProviderJobSearchFilterValues = z.infer<typeof providerJobSearchFilterSchema>;

export const RADIUS_MILES_CHOICES = [10, 25, 50, 100, 250] as const;
export const DEFAULT_RADIUS_MILES = 50;

export const RADIUS_OPTIONS = RADIUS_MILES_CHOICES.map((miles) => ({
  value: String(miles),
  label: `${miles} miles`,
}));

export const DEFAULT_RADIUS_OPTION = {
  value: String(DEFAULT_RADIUS_MILES),
  label: `${DEFAULT_RADIUS_MILES} miles`,
};

export const EMPTY_PROVIDER_JOB_SEARCH_FILTERS: ProviderJobSearchFilterValues = {
  providerCategory: null,
  aoiSpecialty: null,
  subspecialties: [],
  states: [],
  workLocation: [],
  jobType: [],
  employmentClassification: [],
  location: null,
  radiusMiles: DEFAULT_RADIUS_OPTION,
};

/**
 * Serializes structured provider job search filter form values into the
 * CSV/query-param shape the marketplace-search endpoint's
 * `get_job_listing_marketplace_filters` dependency expects. Keys are omitted
 * entirely when the corresponding filter is empty, rather than sent as ''.
 *
 * The radius params go as a set (`nearLat`, `nearLng`, `radiusMiles`): the API refuses a
 * partial set, so they are sent only once a place is picked.
 */
export const serializeProviderJobSearchFilters = (
  values: ProviderJobSearchFilterValues
): Record<string, string> => {
  const params: Record<string, string> = {};

  if (values.providerCategory?.value != null) {
    params.providerCategoryId = String(values.providerCategory.value);
  }

  if (values.aoiSpecialty?.value) {
    params.specialtyId = values.aoiSpecialty.value;
  }

  if (values.subspecialties && values.subspecialties.length > 0) {
    params.subspecialtyIds = values.subspecialties.map((sub) => sub.value).join(',');
  }

  if (values.states && values.states.length > 0) {
    params.stateCodes = values.states.map((state) => state.value).join(',');
  }

  if (values.workLocation && values.workLocation.length > 0) {
    params.workLocations = values.workLocation.map((location) => location.value).join(',');
  }

  if (values.jobType && values.jobType.length > 0) {
    params.jobTypes = values.jobType.map((type) => type.value).join(',');
  }

  if (values.employmentClassification && values.employmentClassification.length > 0) {
    params.employmentClassifications = values.employmentClassification
      .map((type) => type.value)
      .join(',');
  }

  if (values.location) {
    params.nearLat = String(values.location.latitude);
    params.nearLng = String(values.location.longitude);
    params.radiusMiles = values.radiusMiles?.value ?? String(DEFAULT_RADIUS_MILES);
  }

  return params;
};
